/** One tile per Microsoft 365 service, worst state first. */

import { Badge, Card, EmptyState, Statistic, Typography } from "@norican/react";

import type { M365ServiceState } from "@/lib/api";

import { bySeverity, STATE_TONE } from "./status";

interface ServiceTilesProps {
  services: M365ServiceState[];
}

export function ServiceTiles({ services }: ServiceTilesProps) {
  if (services.length === 0) {
    return (
      <Card variant="outline">
        <Card.Body>
          <EmptyState
            size="sm"
            title="No services reported"
            description="Microsoft Graph returned an empty service list for this tenant."
          />
        </Card.Body>
      </Card>
    );
  }

  const ordered = [...services].sort(bySeverity);

  return (
    <section className="flex flex-col gap-3">
      <Typography variant="heading-4" as="h2">
        Services
      </Typography>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {ordered.map((service) => (
          <ServiceTile key={service.id} service={service} />
        ))}
      </div>
    </section>
  );
}

interface ServiceTileProps {
  service: M365ServiceState;
}

function ServiceTile({ service }: ServiceTileProps) {
  const tone = STATE_TONE[service.state];
  const open = service.unresolved_issue_count;

  return (
    <Card variant="outline" accent="left" accentTone={tone}>
      <Card.Body>
        <div className="flex items-start justify-between gap-3">
          <Statistic
            label={service.service}
            value={service.status_label}
            tone={tone}
            size="sm"
            caption={
              service.issue_count === 0
                ? "No issues recorded"
                : `${service.issue_count} ${service.issue_count === 1 ? "issue" : "issues"} tracked`
            }
          />
          {open > 0 && (
            <Badge tone={tone === "neutral" ? "neutral" : tone} variant="solid" size="sm">
              {open} open
            </Badge>
          )}
        </div>
      </Card.Body>
    </Card>
  );
}
